'use client';

import IconLeft from '@/assets/icons/arrow-left.svg';
import IconSearch from '@/assets/icons/icon-search.svg';
import ImagePc from '@/assets/images/Search-images/image-pc.png';
import { Link } from '@/i18n/navigation';
import Image from 'next/image';
import { useClickAway } from 'react-use';
import { Button } from 'rsuite';
import styles from './searchInput.module.scss';

import {
  CategorySortField,
  OrderDirection,
  ProductVariantSortField,
  useCategoryListQuery,
  useSearchTabsQuery,
  useSearchVariantsQuery,
} from '@/graphql';
import convertToReadable from '@/helpers/convertToReadable';
import { decodeBase64Id } from '@/helpers/decodeBase64Id';
import { useDebounce } from '@/helpers/use-debounce';
import { useTranslations } from 'next-intl';
import React, { useEffect, useMemo, useState } from 'react';
import { MdOutlineChevronRight } from 'react-icons/md';
import Skeleton from 'react-loading-skeleton';
import { useLanguageCode } from '@/hooks/useLanguageCode';
import { getLevelOneCategory } from '../search-input';

type CategoryType = {
  name: string;
  slug: string;
  id: string;
};

export const SearchInputMobile = () => {
  const [focused, setFocused] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const ref = React.useRef(null);
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const t = useTranslations('Layout.Header-bottom');
  const searchDebounse = useDebounce(searchQuery, 500);
  const languageCode = useLanguageCode();

  const { data: searchResult, loading } = useSearchVariantsQuery({
    variables: {
      query: searchDebounse ? searchDebounse : undefined,
      first: 3,
      sortBy: {
        direction: OrderDirection.DESC,
        field: searchDebounse
          ? ProductVariantSortField.RANK
          : ProductVariantSortField.POPULARITY,
      },
    },
  });

  const { data: categoryList, loading: categoryListLoading } =
    useCategoryListQuery({
      variables: {
        first: 3,
        filter: { productSearch: searchDebounse },
        languageCode: languageCode,
        sortBy: {
          direction: OrderDirection.DESC,
          field: CategorySortField.POPULARITY,
        },
      },
    });

  const { data: searchTabs, loading: searchTabsLoading } = useSearchTabsQuery({
    variables: {
      first: 6,
      variants: searchResult?.search?.edges?.[0]?.node?.id,
    },
  });

  useEffect(() => {
    if (categoryList?.categories?.edges.length !== 0) {
      const category: CategoryType[] = (
        categoryList?.categories?.edges.map(i => getLevelOneCategory(i.node)) ||
        []
      ).filter(Boolean); // `undefined` yoki `null` bo‘lsa, yo‘q qilamiz

      const uniqueCategory = category.filter(
        (item, index, self) => self.findIndex(t => t.id === item.id) === index
      );

      setCategories(uniqueCategory);
    }
  }, [categoryList, searchQuery]);

  useClickAway(ref, () => {
    setFocused(false);
  });

  const onClickItem = () => {
    setSearchQuery('');
    setFocused(false);
  };

  const customArray = useMemo(() => {
    const focusedString = searchTabs?.searchTabs?.edges;

    return [
      searchQuery,
      searchQuery + ' ' + (focusedString?.[0]?.node?.toLowerCase() || ''),
      searchQuery +
        ' ' +
        (focusedString?.[0]?.node?.toLowerCase() || '') +
        ' ' +
        (focusedString?.[1]?.node?.toLowerCase() || ''),
    ];
  }, [searchQuery, searchTabs?.searchTabs?.edges]);

  return (
    <>
      {focused && <div className={styles.backdrop} />}
      <div
        ref={ref}
        className={`${styles.container} ${focused ? styles.focused : ''}`}
      >
        {focused && (
          <Button
            type="button"
            onClick={() => setFocused(false)}
            className={styles.iconBack}
          >
            <IconLeft />
          </Button>
        )}
        <div className={styles.inputWrapper}>
          <input
            className={`${styles.input} ${focused ? styles.focusedInput : ''}`}
            type="text"
            name="search"
            placeholder={t('search-input-placeholder')}
            onFocus={() => setFocused(true)}
            value={searchQuery}
            onChange={e => {
              e.preventDefault();
              e.stopPropagation();
              setSearchQuery(e.target.value);
            }}
          />
          <IconSearch className={styles.icon} />
        </div>

        <div className={`${styles.dropdown} ${focused ? styles.visible : ''}`}>
          <div className={styles.itemTypes}>
            {searchTabsLoading
              ? Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className={styles.type}>
                    <Skeleton />
                  </div>
                ))
              : searchTabs?.searchTabs?.edges.map(i => (
                  <Button
                    key={i?.node}
                    onClick={() =>
                      setSearchQuery(
                        searchQuery ? searchQuery + ' ' + i?.node : i?.node
                      )
                    }
                    type="button"
                    className={styles.type}
                  >
                    {i?.node}
                  </Button>
                ))}
          </div>
          <div className={styles.phoneTypes}>
            {customArray
              .filter(i => i !== '')
              .map((i, idx) => (
                <Link
                  key={idx}
                  href={`/search/${encodeURIComponent(i)}`}
                  onClick={onClickItem}
                >
                  <div className={styles.dropdownItem}>
                    {i} <MdOutlineChevronRight size={20} />
                  </div>
                </Link>
              ))}
          </div>
          <div className={styles.variants}>
            {loading
              ? Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className={styles.skeletonForMobile}>
                    <Skeleton />
                  </div>
                ))
              : searchResult?.search?.edges?.map(product => {
                  return (
                    <Link
                      key={product?.node?.id}
                      href={`/product/${decodeBase64Id(product.node.id)}-${product.node.product.category?.slug}`}
                      onClick={onClickItem}
                      className={styles.technics}
                    >
                      <div className={styles.itemTech}>
                        <div className={styles.itemInfo}>
                          <div className={styles.cardImageWrapper}>
                            <Image
                              width={64}
                              height={64}
                              src={product.node.media?.[0]?.url || ImagePc}
                              alt="product-photo"
                              className={styles.cardImage}
                            />
                          </div>
                        </div>
                        <div className={styles.itemPrice}>
                          <p className={styles.name}>
                            {product?.node?.name || ''}
                          </p>
                          {product.node.pricing?.discount?.net.amount ? (
                            <>
                              <p className={styles.discountPrice}>
                                {convertToReadable(
                                  product.node.pricing?.price?.net.amount
                                )}{' '}
                                {t('price')}
                              </p>
                              <p className={styles.price}>
                                {convertToReadable(
                                  product.node.pricing?.discount?.net.amount
                                )}{' '}
                                {t('price')}
                              </p>
                            </>
                          ) : (
                            <p className={styles.price}>
                              {convertToReadable(
                                product.node.pricing?.price?.net.amount
                              )}{' '}
                              {t('price')}
                            </p>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
          </div>
          <div className={styles.categories}>
            {categoryListLoading
              ? Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className={styles.skeletonForMobile}>
                    <Skeleton />
                  </div>
                ))
              : categories.map((category, idx) => (
                  <Link
                    href={`/category/${decodeBase64Id(category.id)}-${category.slug}`}
                    key={idx}
                    onClick={onClickItem}
                    className={styles.category}
                  >
                    <div className={styles.itemCategory}>
                      <p>{category.name}</p>
                    </div>
                  </Link>
                ))}
          </div>
        </div>
      </div>
    </>
  );
};
